import React from 'react';
import {
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  FaChartBar,
  FaEye,
  FaTrash,
  FaUserEdit,
  FaPlay,
  FaUser,
  FaFileAlt,
  FaCalendarAlt,
} from 'react-icons/fa';
import { COLORS } from '../../../constants/radar';
import {
  getAssignmentStatus,
  getAssignmentStatusColor,
  getAssignmentStatusText,
  getAssignmentStatusBorderColor,
  getAssignmentStatusTextColor,
  ASSIGNMENT_STATUSES,
} from '../../../constants/assignmentStatuses';
import {
  getReviewTypeColor,
  getReviewTypeTextColor,
  getReviewTypeText
} from '../../../constants/reviewTypes';
import { ReviewTypeBadge } from '../../common/ReviewTypeBadge';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { getRoleChipStyle } from '../../../constants/roleTypes';
import {
  SessionCard,
  SessionCardContent,
} from '../../common/SessionGrid/styles';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { getDurationFromNow } from '../../../utils/dateUtils';
import { toLocalDate } from '../../../utils/dateOnly';

const tooltipConfig = {
  arrow: true,
  placement: 'top',
  enterDelay: 500,
  leaveDelay: 200
};

const AssignmentCardItem = ({
  assignment,
  onView,
  onViewReport,
  onEditReviewers,
  onDelete,
  onPublish,
  ability,
}) => {
  const individualReviewType = assignment.reviewers?.[0]?.reviewType;
  const status = getAssignmentStatus(assignment);

  // ADR-106 §3 (amended): the assignment report is a single destination that serves
  // two audiences — readers (results) and modifiers (the ADR-003 surface it hosts).
  // Two buttons pointing at the same route told the user nothing; these two
  // predicates decide whether the one button shows and how it describes itself.
  const canOpenReport = (
    status === ASSIGNMENT_STATUSES.IN_PROGRESS
    || status === ASSIGNMENT_STATUSES.COMPLETED
    || status === ASSIGNMENT_STATUSES.PENDING_ACTIONS
  ) && ability.can(ACTIONS.READ, SUBJECTS.INDIVIDUAL_ASSIGNMENT);

  // Every status except NOT_STARTED offers at least one modification on the report
  // page, so all four are here. PUBLISHED is the one the read predicate above does
  // NOT cover (nothing answered yet, so no results to read — but reviewers and
  // matrix can still be corrected). COMPLETED looks read-only but is not: structural
  // changes are blocked there while "Reopen Start/Stop/Continue" is enabled ONLY
  // there (`canReopenActions` in FeedbackSessionModifications). NOT_STARTED is
  // excluded because the bulk editor owns it (ADR-106 §2/§3).
  const canModifyFromReport = status !== ASSIGNMENT_STATUSES.NOT_STARTED
    && ability.can(ACTIONS.MANAGE, SUBJECTS.FEEDBACK_SESSION_MODIFICATION);

  const reportButtonTooltip = canOpenReport && canModifyFromReport
    ? 'View assignment report - see the results and modify the assignment'
    : canOpenReport
      ? 'View assignment report'
      : 'Modify assignment - change reviewers, matrix or actions from the assignment report';

  const matrix = assignment.skillsMatrix;
  const reviewProcess = assignment.reviewProcess?.[0];

  return (
    <SessionCard>
      <SessionCardContent>
        {/* Employee Avatar and Full Name */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 2,
          mt: 1
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <EmployeeAvatar
                  employee={assignment.employee}
                  size={40}
                />
                <Box>
                  <Typography sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: COLORS.PRIMARY,
                    lineHeight: 1.3
                  }}>
                    {assignment.employee?.fullName || 'Unknown Individual'}
                  </Typography>
                  <Typography sx={{
                    fontSize: '12px',
                    color: '#64748B',
                    lineHeight: 1.3
                  }}>
                    {assignment.employee?.orgPositions?.label || 'No Position'}
                    {assignment.employee?.orgGrades?.label &&
                      <> • {assignment.employee.orgGrades.label}</>
                    }
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={getAssignmentStatusText(assignment)}
                sx={{
                  backgroundColor: getAssignmentStatusColor(assignment),
                  color: getAssignmentStatusTextColor(assignment),
                  fontWeight: 500,
                  fontSize: '13px',
                  border: '1px solid',
                  borderColor: getAssignmentStatusBorderColor(assignment),
                  '& .MuiChip-label': {
                    px: 2
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{
          borderTop: '1px solid #E2E8F0',
          pt: 2,
          mt: 1
        }} />

        {/* Individual Assignment Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 2,
          mt: 1
        }}>
          <Typography sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <FaUser size={12} style={{ color: '#64748B' }} />
            Individual Assignment
          </Typography>
          {individualReviewType && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 1
            }}>
              <ReviewTypeBadge
                reviewType={individualReviewType}
                getReviewTypeColor={getReviewTypeColor}
                getReviewTypeTextColor={getReviewTypeTextColor}
                getReviewTypeText={getReviewTypeText}
              />
            </Box>
          )}

          {/* Session Template and Deadline Information */}
          {reviewProcess && reviewProcess.skillsMatrixSession && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mt: 0,
              fontSize: '13px',
              color: '#64748B',
            }}>
              {/* Session Template */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FaFileAlt size={14} style={{ color: '#94A3B8' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '14px' }}>
                    {reviewProcess.skillsMatrixSession.name}
                  </Typography>
                  {reviewProcess.skillsMatrixSession.version && (
                    <Chip
                      label={`v${reviewProcess.skillsMatrixSession.version}`}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        height: '20px',
                        backgroundColor: '#F1F5F9',
                        color: '#475569',
                        border: '1px solid #E2E8F0',
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Deadline Information */}
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1
              }}>
                <FaCalendarAlt size={14} style={{ color: '#94A3B8', flexShrink: 0, marginTop: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontSize: '14px' }}>
                    Deadline: {toLocalDate(reviewProcess.deadlineAt)?.toLocaleDateString()}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#64748B',
                      fontStyle: 'italic'
                    }}
                  >
                    {getDurationFromNow(reviewProcess.deadlineAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{
          borderTop: '1px solid #E2E8F0',
          pt: 2,
          mt: 1
        }} />

        {/* Skills Matrix Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 2
        }}>
          <Box>
            <Typography sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5
            }}>
              Skills Matrix Applied
            </Typography>
            <Typography sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: COLORS.PRIMARY,
              lineHeight: 1.3,
              mb: 2
            }}>
              {matrix?.name || 'No Matrix'}
            </Typography>

            {matrix?.position && (
              <>
                <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 0.5
                }}>
                  Position
                </Typography>
                <Box sx={{
                  display: 'flex',
                  mb: 2
                }}>
                  <Chip
                    label={matrix.position.label}
                    size="small"
                    sx={{
                      fontSize: '13px',
                      backgroundColor: '#F1F5F9',
                      color: '#475569',
                      border: '1px solid #E2E8F0',
                      height: '24px',
                      '& .MuiChip-label': {
                        px: 1.5
                      }
                    }}
                  />
                </Box>
              </>
            )}

            {matrix?.matrixRoles && matrix.matrixRoles.length > 0 && (
              <>
                <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1
                }}>
                  Roles
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {matrix.matrixRoles.map(role => {
                    const { bgColor, textColor } = getRoleChipStyle(role.role.type);
                    return (
                      <div
                        key={role.id}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '16px',
                          backgroundColor: bgColor,
                          fontSize: '13px',
                          color: textColor,
                          fontWeight: '500',
                          border: `1px solid ${textColor}20`,
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}
                      >
                        {role.role.label}
                      </div>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          pt: 3,
          borderTop: '1px solid #E2E8F0',
          gap: 1
        }}>
          {/* Left side - Publish action */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {status === ASSIGNMENT_STATUSES.NOT_STARTED && ability.can(ACTIONS.PUBLISH, SUBJECTS.INDIVIDUAL_ASSIGNMENT) && (
              <Tooltip
                title="Publish to In Progress - Makes the assignment available to assigned individual and reviewers"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPublish(assignment);
                  }}
                  sx={{
                    backgroundColor: '#E0F2FE',
                    border: '1px solid #7DD3FC',
                    '&:hover': {
                      backgroundColor: '#BAE6FD',
                    }
                  }}
                >
                  <FaPlay size={14} color="#0284C7" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Right side - Other actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip
              title="View assignment details"
              {...tooltipConfig}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onView(assignment);
                }}
                sx={{
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  '&:hover': {
                    backgroundColor: '#DCFCE7',
                  }
                }}
              >
                <FaEye size={14} color="#22C55E" />
              </IconButton>
            </Tooltip>

            {/* One destination, one button (ADR-106 §3 as amended): the report page
                both shows the results and hosts the ADR-003 modification surface, so
                reading and modifying are the same navigation. The tooltip names
                whichever of the two this user can actually do there. */}
            {(canOpenReport || canModifyFromReport) && (
              <Tooltip
                title={reportButtonTooltip}
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewReport(assignment);
                  }}
                  sx={{
                    backgroundColor: '#F0F7FF',
                    border: '1px solid #BFDBFE',
                    '&:hover': {
                      backgroundColor: '#DBEAFE',
                    }
                  }}
                >
                  <FaChartBar size={14} color="#3B82F6" />
                </IconButton>
              </Tooltip>
            )}

            {/* ADR-106 §3: the bulk reviewer editor is a scheduling tool — offered only
                before the assignment is published, mirroring SessionCardItem. */}
            {(status === ASSIGNMENT_STATUSES.NOT_STARTED &&
              ability.can(ACTIONS.UPDATE, SUBJECTS.INDIVIDUAL_ASSIGNMENT)) && (
              <Tooltip
                title="Edit reviewers - Change the reviewers and templates before the assignment is published"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditReviewers(assignment);
                  }}
                  sx={{
                    backgroundColor: '#F0F7FF',
                    border: '1px solid #BFDBFE',
                    '&:hover': {
                      backgroundColor: '#DBEAFE',
                    }
                  }}
                >
                  <FaUserEdit size={14} color="#3B82F6" />
                </IconButton>
              </Tooltip>
            )}

            {status === ASSIGNMENT_STATUSES.NOT_STARTED && ability.can(ACTIONS.DELETE, SUBJECTS.INDIVIDUAL_ASSIGNMENT) && (
              <Tooltip
                title="Delete assignment"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(assignment);
                  }}
                  sx={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    '&:hover': {
                      backgroundColor: '#FEE2E2',
                    }
                  }}
                >
                  <FaTrash size={14} color="#EF4444" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </SessionCardContent>
    </SessionCard>
  );
};

export default AssignmentCardItem;
